import type { LucideIcon } from "lucide-react";
import {
  CalendarClock,
  CheckCircle,
  Link2,
  Scissors,
  Smartphone,
  UserCheck,
} from "lucide-react";
import {
  PRIMARY_CTA_HREF,
  PRIMARY_CTA_LABEL,
  PRICING_PLANS,
} from "@/components/home/homeContent";

export const AGENDA_LANDING_PATH = "/agenda-online-para-barbearia";

export const AGENDA_LANDING_H1 =
  "Agenda online para barbearia: organize seus horários e agendamentos";

export const AGENDA_SECTION_IDS = {
  comoFunciona: "como-funciona",
  recursos: "recursos",
  beneficios: "beneficios",
  experienciaCliente: "experiencia-cliente",
  planos: "planos",
  faq: "faq",
} as const;

export { PRIMARY_CTA_HREF, PRIMARY_CTA_LABEL, PRICING_PLANS };

export const AGENDA_LANDING_NAV = [
  { label: "Como funciona", href: `#${AGENDA_SECTION_IDS.comoFunciona}` },
  { label: "Recursos", href: `#${AGENDA_SECTION_IDS.recursos}` },
  { label: "Planos", href: `#${AGENDA_SECTION_IDS.planos}` },
  { label: "FAQ", href: `#${AGENDA_SECTION_IDS.faq}` },
];

export const PAIN_POINTS = [
  "Horários anotados em papel, WhatsApp ou planilha",
  "Dificuldade para ver a agenda do dia de forma clara",
  "Marcações manuais que geram conflito entre profissionais",
  "Cliente precisa esperar resposta para saber disponibilidade",
];

export type HowItWorksStep = {
  step: string;
  title: string;
  description: string;
};

/** Fluxo real: configuração no painel + portal público de agendamento. */
export const HOW_IT_WORKS: HowItWorksStep[] = [
  {
    step: "1",
    title: "Cadastre serviços e barbeiros",
    description:
      "Configure cortes, duração, valores e profissionais no painel da barbearia.",
  },
  {
    step: "2",
    title: "Organize a disponibilidade",
    description:
      "Defina horários de atendimento e visualize a agenda por profissional.",
  },
  {
    step: "3",
    title: "Compartilhe o link de agendamento",
    description:
      "Cada barbearia recebe um acesso público para clientes consultarem horários.",
  },
  {
    step: "4",
    title: "Cliente agenda pelo celular",
    description:
      "No portal, o cliente escolhe serviço, barbeiro (quando aplicável), data e horário.",
  },
  {
    step: "5",
    title: "Agendamento na sua agenda",
    description:
      "A marcação aparece no painel para você gerenciar, confirmar ou ajustar.",
  },
];

export type AgendaFeature = {
  title: string;
  description: string;
  icon: LucideIcon;
};

export const AGENDA_FEATURES: AgendaFeature[] = [
  {
    title: "Agenda por profissional",
    description:
      "Visualize horários por barbeiro e organize a operação com mais clareza.",
    icon: UserCheck,
  },
  {
    title: "Horários disponíveis",
    description:
      "O sistema considera duração do serviço e ocupação para sugerir horários livres.",
    icon: CalendarClock,
  },
  {
    title: "Conflitos evitados",
    description:
      "Marcações respeitam disponibilidade já ocupada na agenda da barbearia.",
    icon: CheckCircle,
  },
  {
    title: "Serviços com duração",
    description:
      "Cadastre serviços com tempo estimado para encaixar corretamente na agenda.",
    icon: Scissors,
  },
  {
    title: "Link de agendamento",
    description:
      "Compartilhe um endereço público para clientes agendarem sem depender de mensagens.",
    icon: Link2,
  },
  {
    title: "Acesso pelo celular",
    description:
      "Gerencie a agenda pelo navegador, no celular ou computador, sem instalar app.",
    icon: Smartphone,
  },
];

export const BARBER_BENEFITS = [
  "Visão clara dos horários do dia",
  "Menos idas e vindas para confirmar horário",
  "Agenda centralizada no painel da barbearia",
  "Organização por barbeiro nos planos com equipe",
  "Histórico de atendimentos vinculado aos agendamentos",
  "Menos trabalho manual para marcar cliente",
];

export const CUSTOMER_EXPERIENCE = [
  "Consultar horários disponíveis online",
  "Escolher serviço e profissional quando disponível",
  "Agendar pelo celular, a qualquer hora",
  "Consultar agendamentos pelo portal público",
  "Reagendar ou cancelar conforme regras da barbearia",
];

export type FaqItem = { question: string; answer: string };

export const AGENDA_FAQ: FaqItem[] = [
  {
    question: "Como funciona a agenda online para barbearia?",
    answer:
      "Você configura serviços, barbeiros e horários no painel. A agenda organiza os atendimentos e você pode compartilhar um link para clientes marcarem online.",
  },
  {
    question: "O cliente consegue fazer o próprio agendamento?",
    answer:
      "Sim. Cada barbearia pode disponibilizar uma página pública em que o cliente escolhe serviço, data e horário disponível.",
  },
  {
    question: "Posso organizar horários por barbeiro?",
    answer:
      "Sim. Nos planos com mais de um profissional, a agenda considera a disponibilidade de cada barbeiro.",
  },
  {
    question: "Posso cadastrar meus serviços?",
    answer:
      "Sim. Você cadastra serviços com duração e valor, e eles aparecem no fluxo de agendamento.",
  },
  {
    question: "Preciso instalar aplicativo?",
    answer:
      "Não. O Meu Barbeiro PRO funciona no navegador do celular ou computador.",
  },
  {
    question: "Quanto custa?",
    answer:
      "Planos a partir de R$ 29,90/mês após o teste grátis de 7 dias. Consulte os planos na página para ver as opções.",
  },
  {
    question: "Existe período de teste?",
    answer:
      "Sim. Você pode testar grátis por 7 dias com acesso às funcionalidades premium antes de assinar.",
  },
];

export const PUBLIC_BOOKING_EXAMPLE =
  "meubarbeiropro.com/agendar/sua-barbearia";
